import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { type, ShopState } from './state';
import { shopService, GetProductsResponse, CheckoutResponse, CheckProformaResponse, SubmitWithoutPaymentResponse } from "../../services";
import { createClearAllAsyncThunk, createClearErrorAsyncThunk } from "../shared-actions";
import { CartItem } from "../../models/shop.model";

export const getProductsAction = createAsyncThunk<GetProductsResponse, { customerId: number }>(
    `${type}/getProducts`,
    async (args, { rejectWithValue }) => {
        try {
            return await shopService.getProducts(args.customerId);
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const checkProformaStatusAction = createAsyncThunk<CheckProformaResponse, { customerId: number, proformaId: string }>(
    `${type}/checkProformaStatus`,
    async (args, { rejectWithValue }) => {
        try {
            return await shopService.checkProformaStatus(args.customerId, args.proformaId);
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const checkoutAction = createAsyncThunk<CheckoutResponse, { customerId: number, cart: CartItem[] }>(
    `${type}/checkout`,
    async (args, { rejectWithValue }) => {
        try {
            return await shopService.checkout(args.customerId, args.cart);
        } catch (error) {
            return rejectWithValue(error);
        }
    },
    {
        // Double-submit guard (F33): the button's `disabled={isFetching}` flips
        // one render late, so two same-frame taps both dispatch and the API mints
        // a fresh proforma per call (orphan pending order). Abort while a shop
        // request is already in flight.
        condition: (_arg, { getState }) => !(getState() as { shop: ShopState }).shop.isFetching,
    }
);

export const submitWithoutPaymentAction = createAsyncThunk<SubmitWithoutPaymentResponse, { customerId: number, cart: CartItem[] }>(
    `${type}/submitWithoutPayment`,
    async (args, { rejectWithValue }) => {
        try {
            return await shopService.submitWithoutPayment(args.customerId, args.cart);
        } catch (error) {
            return rejectWithValue(error);
        }
    },
    {
        // Same double-submit guard as checkout (F33): abort a pay-later submit
        // while a shop request is already in flight.
        condition: (_arg, { getState }) => !(getState() as { shop: ShopState }).shop.isFetching,
    }
);

// Cart Actions
export const addToCartAction = createAction<CartItem>(`${type}/addToCart`);
export const removeFromCartAction = createAction<string>(`${type}/removeFromCart`); // By CartItem.id
export const updateCartItemAction = createAction<{ id: string; quantity: number }>(`${type}/updateCartItem`);
export const updateCartItemLearnerAction = createAction<{ id: string; learnerId: number | null }>(`${type}/updateCartItemLearner`);
export const clearCartAction = createAction(`${type}/clearCart`);
export const clearProformaStatusAction = createAction(`${type}/clearProformaStatus`);
// Records the pending proforma from a guest storefront checkout so its paymentUrl
// can be resurfaced as a pay-again/retry path (survives the payment redirect via
// the persisted shop slice). An empty payload clears it (e.g. on confirmed payment).
export const setPendingProformaAction = createAction<{ paymentUrl?: string; proforma?: any }>(`${type}/setPendingProforma`);

export const clearErrorsAction = createClearErrorAsyncThunk(type);
export const clearAllAction = createClearAllAsyncThunk(type);
